import { Steps } from 'intro.js-react';
import 'intro.js/introjs.css';
import * as React from 'react';

import * as ReactDOMServer from 'react-dom/server';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { batch, connect } from 'react-redux';
import { Dispatch } from 'redux';
import fetchMock from 'fetch-mock';

import { closeTab, createNewTab } from '../../actions/EditorActions/EditorActions';
import {
  walkthroughAddCode,
  walkthroughInProgress,
  walkthroughOutProgress,
  walkthroughRemoveCode,
  walkthroughWithoutChartProgress,
} from '../../actions/WalkthroughAction';
import { Walkthrough } from '../../components/Walkthrough';
import { StateType } from '../../reducers';
import { getUserSettingsSelector } from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import { getLastIndexFromTabsSelector } from '../../reducers/EditorReducer/EditorSelector';
import { intlFormatMessage } from '../../shared/helpers';
import { EVENT_ATTRIBUTE, EVENT_LABEL_ATTRIBUTE, formatHTMLMessageHelper } from '../../shared/helpers/internationalization';
import { DispatchConnect } from '../../types';

import setupEditorIntroMocks from './mocks/EditorIntro/setup';
import analytics from '../../shared/services/analytics/analytics';
import { updateCustomPreferencesState } from '../../reducers/CustomPreferencesReducer/CustomPreferencesReducer';

type Step = Steps['props']['steps'][number];

interface ExtendedStep extends Step {
  isClickElementToContinue?: boolean;
  isEnabledInteraction?: boolean;
}

interface EditorMainState {}

interface EditorIntroProps {
  userId: string;
  handleOnExitIntro: () => void;
}

interface EditorIntroStateDispatchers {
  dispatchCreateNewTab: (data: any) => void;
  dispatchWalkthroughAddCode: () => void;
  dispatchWalkthroughRemoveCode: () => void;
  dispatchWalkthroughInProgress: () => void;
  dispatchWalkthroughOutProgress: () => void;
  dispatchWalkthroughWithoutChartProgress: () => void;
  dispatchCloseTab: (closeTabIndex: number) => void;
  dispatchUpdateWalkthroughState: (isComplete: boolean) => void;
}

interface EditorIntroStateProps {
  userSettings: any;
  currentIndexTab: number;
}

type EditorIntroPropsGeneric = InjectedIntlProps & EditorIntroProps & EditorIntroStateProps & EditorIntroStateDispatchers;

class EditorIntroIntl extends React.Component<EditorIntroPropsGeneric, EditorMainState> {
  private isCompleted: boolean = false;
  private isCleanedUp: boolean = false;
  private previousStep = 0;

  private walkthroughSteps: Steps | null = null;
  private containerRef: React.RefObject<HTMLDivElement | null> = React.createRef<HTMLDivElement>();
  private introJSContainer: HTMLDivElement | null = null;
  private introJSDisableInteraction: HTMLDivElement | null = null;

  componentDidMount() {
    // Add mocks for alpha simulation
    setupEditorIntroMocks();

    this.introJSContainer = document.querySelector('.introjs-tooltipReferenceLayer');
    this.props.dispatchCreateNewTab(this.props.userSettings);

    //IntroJS removes all handlers from links, so we add them to body:
    document.querySelector('body')?.addEventListener('click', this.handleClickExternalLink);
  }

  componentWillUnmount() {
    document.querySelector('body')?.removeEventListener('click', this.handleClickExternalLink);
  }

  handleClickExternalLink = (e: MouseEvent): void => {
    const target = e.target as HTMLElement;
    if (target.hasAttribute(EVENT_ATTRIBUTE)) {
      const eventName = target.getAttribute(EVENT_ATTRIBUTE);
      const eventLabel = target.getAttribute(EVENT_LABEL_ATTRIBUTE);
      if (eventName) {
        analytics.trackCustomEvent(eventName, {
          event_label: eventLabel as string,
        });
      }
    }
  };
  handleOnComplete = () => {
    this.isCompleted = true;
    analytics.trackCustomEvent('sim_walkthrough_click_done', { event_label: '9' });
  };

  handleOnExit = () => {
    // handleOnExit is being triggered multiple times
    // But we want to do clean up only once
    if (!this.isCleanedUp) {
      this.isCleanedUp = true;

      // Track skip event only if user didn't complete the walkthrough
      if (!this.isCompleted) {
        analytics.trackCustomEvent('sim_walkthrough_click_skip', {
          event_label: String(this.previousStep + 1),
        });
      }

      // Restore native implementation of `fetch`
      fetchMock.restore();

      this.props.dispatchUpdateWalkthroughState(true);

      this.props.handleOnExitIntro();

      // In case we want use click element to continue for the last step
      // We still need to clean it up on exit because onBeforeChange will not be called anymore
      this.resetClickElementToContinue();

      batch(() => {
        this.props.dispatchCloseTab(this.props.currentIndexTab);
        // If user completed the walkthrough, we want to keep the example code for users to play around with.
        // Otherwise, if user skipped the walkthrough, we just close the tab
        if (this.isCompleted) {
          this.props.dispatchWalkthroughAddCode();
        }
      });
    }
  };

  handleRefStep = (ref: Steps) => {
    this.walkthroughSteps = ref;
  };

  // Since intro.js doesn't support setOption in runtime
  // https://github.com/usablica/intro.js/issues/1414#issuecomment-962427013
  // We need to manipulate with DOM to hide next button
  // for steps we want user to click to the target element to continue
  toggleDisplayNextButton = (isDisplayed: boolean) => {
    if (this.introJSContainer === null) {
      return;
    }

    const classList = this.introJSContainer.classList;
    const hideNextButtonClassName = 'introjs-tooltipReferenceLayer--hide-next-btn';

    const isNextButtonHidden = classList.contains(hideNextButtonClassName);
    if (isDisplayed && isNextButtonHidden) {
      classList.remove(hideNextButtonClassName);
    } else if (!isDisplayed && !isNextButtonHidden) {
      classList.add(hideNextButtonClassName);
    }
  };

  handleClickDisableInteraction = () => {
    const currentStepIndex = this.previousStep;

    // Track specific action events for click-element-to-continue steps
    switch (currentStepIndex) {
      case 3: // Step 4: Example button
        analytics.trackCustomEvent('sim_walkthrough_click_example', { event_label: '4' });
        break;
      case 4: // Step 5: Simulate button
        analytics.trackCustomEvent('sim_walkthrough_click_simulate', { event_label: '5' });
        break;
      case 6: // Step 7: Show test period button
        analytics.trackCustomEvent('sim_walkthrough_click_show_test_period', { event_label: '7' });
        break;
    }

    this.walkthroughSteps?.introJs.nextStep();
  };

  // I think we don't want to update reducers to support walkthrough
  // That's why I tried to listen on click events on disable interaction layer
  setupClickElementToContinue = () => {
    this.introJSDisableInteraction = document.querySelector('.introjs-disableInteraction');
    if (this.introJSDisableInteraction) {
      // Hide the next button in the walkthrough popover
      this.toggleDisplayNextButton(false);

      // Add pointer style for the disable interaction element
      this.introJSDisableInteraction.style.cursor = 'pointer';

      // Listen on click events to go to the next step
      this.introJSDisableInteraction.addEventListener('click', this.handleClickDisableInteraction);
    }
  };

  resetClickElementToContinue = () => {
    // Display the next button as default
    this.toggleDisplayNextButton(true);

    // Stop listen on click events of the disable interaction element
    if (this.introJSDisableInteraction) {
      this.introJSDisableInteraction.removeEventListener('click', this.handleClickDisableInteraction);
    }
  };

  // Since intro.js doesn't support setOption in runtime
  // https://github.com/usablica/intro.js/issues/1414#issuecomment-962427013
  // We need to remove disable interaction layer, and it will be re-created in the next step
  enableInteraction = () => {
    this.introJSDisableInteraction = document.querySelector('.introjs-disableInteraction');
    if (this.introJSDisableInteraction) {
      this.introJSDisableInteraction.remove();
    }
  };

  clickSimulate = () => {
    const simulateButton = document.querySelector('.intro-step-5, .editor-simulate-button-text');
    if (simulateButton) {
      (simulateButton as HTMLButtonElement).click();
    }
    const resultsButton = document.querySelector('.editor__checkbox-results');
    if (resultsButton && !resultsButton.classList.contains('editor__checkbox--selected')) {
      (resultsButton as HTMLButtonElement).click();
    }
  };

  clickShowTestPeriod = () => {
    const testPeriodButton = document.querySelector('.intro-step-7-1');
    if (testPeriodButton) {
      (testPeriodButton as HTMLButtonElement).click();
    }
  };

  onBeforeChange = (nextStepIndex: any) => {
    this.resetClickElementToContinue();

    // Track continue event for regular steps (not click-element-to-continue steps)
    // Click-element-to-continue steps (indices 3, 4, 6) are tracked in handleClickDisableInteraction
    const isClickElementStep = [3, 4, 6].includes(this.previousStep);
    const isMovingForward = nextStepIndex > this.previousStep;
    if (isMovingForward && !isClickElementStep) {
      analytics.trackCustomEvent('sim_walkthrough_click_continue', {
        event_label: String(this.previousStep + 1),
      });
    }

    switch (nextStepIndex) {
      case 3:
        this.props.dispatchWalkthroughRemoveCode();
        break;
      case 4:
        this.props.dispatchWalkthroughAddCode();
        this.props.dispatchWalkthroughWithoutChartProgress();
        break;
      case 5:
        this.clickSimulate();
        break;
      case 6:
        if (this.previousStep === 7) {
          this.clickShowTestPeriod();
        }
        this.walkthroughSteps?.updateStepElement(nextStepIndex);

        break;
      case 7:
        if (this.previousStep === 6) {
          this.clickShowTestPeriod();
        }

        this.walkthroughSteps?.updateStepElement(nextStepIndex);
        break;
      case 8:
        this.walkthroughSteps?.updateStepElement(nextStepIndex);
        break;
      default:
        return;
    }
  };

  onAfterChange = (newStepIndex: number) => {
    const step = this.walkthroughSteps?.props.steps[newStepIndex] as ExtendedStep;
    const isEnabledInteraction = step?.isEnabledInteraction;
    if (isEnabledInteraction) {
      this.enableInteraction();
    }

    const isClickElementToContinue = step?.isClickElementToContinue;
    if (isClickElementToContinue && !isEnabledInteraction) {
      this.setupClickElementToContinue();
    }

    // We currently enable interaction in step 7/8
    // We only want user to be able to interact with the chart
    // That's why we block scrolling here
    if (newStepIndex === 6) {
      const simulationResultsContainer = document.getElementById('alphas-details-sections');
      if (simulationResultsContainer) {
        simulationResultsContainer.style.overflowY = 'hidden';
      }
    }
    this.previousStep = newStepIndex;
  };

  getSteps() {
    const { intl } = this.props;

    const stepCount = 9;
    return [
      {
        element: '.inital-intro',
        intro: ReactDOMServer.renderToString(
          <Walkthrough
            current_step={1}
            total_steps={8}
            title={intlFormatMessage('intro.simulation.1.title', intl)}
            content={
              <p>
                <p>{intlFormatMessage('intro.simulation.1.content', intl)}</p>
              </p>
            }
          />
        ),
      },
      {
        // We have two elements have class intro-step-2, one for desktop screen
        // and other one for mobile screen. Notice that at the same time, only one
        // element is displayed. So that, we need to find the displaying one on the
        // current screen.
        element: [...(document.querySelectorAll('.intro-step-2') as any)].filter(
          node => node instanceof HTMLElement && node.offsetParent !== null
        )[0],
        //position: 'bottom-left-aligned',
        tooltipClass: 'simulation-setting-intro__tooltip',
        /**
         * CSS class of the helperLayer.
         */
        highlightClass: 'simulation-setting-intro__highlight',

        intro: ReactDOMServer.renderToStaticMarkup(
          <Walkthrough
            className={'simulation-setting-intro__content'}
            current_step={2}
            total_steps={stepCount}
            title={intlFormatMessage('intro.simulation.2.title', intl)}
            content={
              <p>
                {formatHTMLMessageHelper(intl, 'intro.simulation.2.content.1')}
                <p>{intlFormatMessage('intro.simulation.2.content.2', intl)}</p>
              </p>
            }
          />
        ),
      },
      {
        element: '.intro-step-3',
        intro: ReactDOMServer.renderToStaticMarkup(
          <Walkthrough
            current_step={3}
            total_steps={stepCount}
            title={intlFormatMessage('intro.simulation.3.title', intl)}
            content={
              <p>
                <p>{formatHTMLMessageHelper(intl, 'intro.simulation.3.content')}</p>
              </p>
            }
          />
        ),
      },
      {
        element: '.intro-step-4',
        isClickElementToContinue: true,
        intro: ReactDOMServer.renderToStaticMarkup(
          <Walkthrough
            current_step={4}
            total_steps={stepCount}
            title={intlFormatMessage('intro.simulation.4.title', intl)}
            content={<p>{formatHTMLMessageHelper(intl, 'intro.simulation.4.content')}</p>}
          />
        ),
      },
      {
        element: '.intro-step-5',
        isClickElementToContinue: true,
        intro: ReactDOMServer.renderToStaticMarkup(
          <Walkthrough
            current_step={5}
            total_steps={stepCount}
            title={intlFormatMessage('intro.simulation.5.title', intl)}
            content={<p>{formatHTMLMessageHelper(intl, 'intro.simulation.5.content')}</p>}
          />
        ),
      },
      {
        intro: ReactDOMServer.renderToStaticMarkup(
          <Walkthrough
            current_step={6}
            total_steps={stepCount}
            title={intlFormatMessage('intro.simulation.6.title', intl)}
            content={<p>{formatHTMLMessageHelper(intl, 'intro.simulation.6.content')}</p>}
          />
        ),
      },
      {
        element: '.intro-step-7-1',
        isClickElementToContinue: true,
        intro: ReactDOMServer.renderToStaticMarkup(
          <Walkthrough
            current_step={7}
            total_steps={stepCount}
            title={formatHTMLMessageHelper(intl, 'intro.simulation.7.1.title')}
            content={
              <p>
                {formatHTMLMessageHelper(intl, 'intro.simulation.7.1.content.1')}
                <p>{formatHTMLMessageHelper(intl, 'intro.simulation.7.1.content.2')}</p>
              </p>
            }
          />
        ),
      },
      {
        element: '.intro-step-7',
        isEnabledInteraction: true,
        intro: ReactDOMServer.renderToStaticMarkup(
          <Walkthrough
            current_step={8}
            total_steps={stepCount}
            title={formatHTMLMessageHelper(intl, 'intro.simulation.7.title')}
            content={
              <p>
                {formatHTMLMessageHelper(intl, 'intro.simulation.7.content.1')}
                <p>{formatHTMLMessageHelper(intl, 'intro.simulation.7.content.2')}</p>
              </p>
            }
          />
        ),
      },
      {
        element: '.editor-bottom-bar__bottom-buttons',
        intro: ReactDOMServer.renderToStaticMarkup(
          <Walkthrough
            current_step={stepCount}
            total_steps={stepCount}
            title={intlFormatMessage('intro.simulation.8.title', intl)}
            content={<p>{formatHTMLMessageHelper(intl, 'intro.simulation.8.content')}</p>}
          />
        ),
      },
    ];
  }

  render() {
    return (
      <div ref={this.containerRef}>
        <Steps
          ref={this.handleRefStep}
          steps={this.getSteps()}
          enabled={true}
          initialStep={0}
          onExit={this.handleOnExit}
          onComplete={this.handleOnComplete}
          onBeforeChange={this.onBeforeChange}
          onAfterChange={this.onAfterChange}
          options={{
            tooltipClass: 'introjs-websim',
            hidePrev: true,
            hideNext: true,
            showBullets: false,
            showStepNumbers: false,
            keyboardNavigation: false,
            exitOnOverlayClick: false,
            nextLabel: 'Continue',
            prevLabel: 'Previous',
            disableInteraction: true,
          }}
        />
      </div>
    );
  }
}

const mapStateToProps = (state: StateType): EditorIntroStateProps => ({
  userSettings: getUserSettingsSelector(state),
  currentIndexTab: getLastIndexFromTabsSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<any>): EditorIntroStateDispatchers => ({
  dispatchCreateNewTab: (data: any) => dispatch(createNewTab(data)),
  dispatchWalkthroughAddCode: () => dispatch(walkthroughAddCode()),
  dispatchWalkthroughRemoveCode: () => dispatch(walkthroughRemoveCode()),
  dispatchWalkthroughInProgress: () => dispatch(walkthroughInProgress()),
  dispatchWalkthroughOutProgress: () => dispatch(walkthroughOutProgress()),
  dispatchWalkthroughWithoutChartProgress: () => dispatch(walkthroughWithoutChartProgress()),
  dispatchCloseTab: (closeTabIndex: number) => dispatch(closeTab(closeTabIndex)),
  dispatchUpdateWalkthroughState: (isComplete: boolean) => dispatch(updateCustomPreferencesState({ simulationWalkthrough: isComplete })),
});

export default injectIntl(
  connect<EditorIntroStateProps, DispatchConnect<EditorIntroStateDispatchers>, any, StateType>(
    mapStateToProps,
    mapDispatchToProps
  )(EditorIntroIntl)
);
